"use client"

/**
 * Keyboard shortcuts for admin forms (Lane D).
 *
 *   Ctrl/Cmd + S         → onSaveDraft  (save as draft)
 *   Ctrl/Cmd + Enter     → onPrimary    (publish / update)
 *   Esc                  → onCancel     (back / close, with guard)
 *
 * Bindings are skipped while focus is inside CKEditor, Quill, or any
 * `contenteditable` so we don't steal in-editor commands like Ctrl+S.
 */

import { useEffect } from "react"

interface UseFormShortcutsOptions {
  enabled?: boolean
  onSaveDraft?: () => void
  onPrimary?: () => void
  onCancel?: () => void
}

const isInsideEditor = (target: EventTarget | null): boolean => {
  if (!(target instanceof Element)) return false
  if (target.closest('[contenteditable="true"]')) return true
  if (target.closest(".ck-content")) return true
  if (target.closest(".ql-editor")) return true
  return false
}

const isMod = (e: KeyboardEvent): boolean => {
  // macOS uses Cmd, Windows/Linux use Ctrl. Treat both as the same modifier.
  return e.metaKey || e.ctrlKey
}

export function useFormShortcuts({
  enabled = true,
  onSaveDraft,
  onPrimary,
  onCancel,
}: UseFormShortcutsOptions) {
  useEffect(() => {
    if (!enabled) return

    const handler = (e: KeyboardEvent) => {
      // Don't capture shortcuts while user is typing in a rich-text editor.
      if (isInsideEditor(e.target)) return

      if (isMod(e) && e.key.toLowerCase() === "s") {
        if (onSaveDraft) {
          e.preventDefault()
          onSaveDraft()
        }
        return
      }
      if (isMod(e) && e.key === "Enter") {
        if (onPrimary) {
          e.preventDefault()
          onPrimary()
        }
        return
      }
      if (e.key === "Escape") {
        if (onCancel) {
          // Don't preventDefault — Esc may also close modals; let antd handle.
          onCancel()
        }
      }
    }

    window.addEventListener("keydown", handler)
    return () => window.removeEventListener("keydown", handler)
  }, [enabled, onSaveDraft, onPrimary, onCancel])
}
